import React from "react";
import { Box, Typography, Paper, List, ListItem, ListItemText, Chip, Avatar } from "@mui/material";
import { LocationOn, TrendingUp } from "@mui/icons-material";

const TopRegionsCard = ({ topRegions = [], isLoading = false }) => {
    if (isLoading) {
        return (
            <Paper sx={{ p: 3, height: 300 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Top Performing Regions
                </Typography>
                <Box
                    sx={{
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (topRegions.length === 0) {
        return (
            <Paper sx={{ p: 3, height: 300 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Top Performing Regions
                </Typography>
                <Box
                    sx={{
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <LocationOn sx={{ color: "#666", fontSize: 48 }} />
                    <Typography variant="body2" color="text.secondary">
                        No region data available
                    </Typography>
                </Box>
            </Paper>
        );
    }

    // Calculate max values for percentage calculations
    const maxOrders = Math.max(...topRegions.map((region) => region.orders));
    const maxRevenue = Math.max(...topRegions.map((region) => parseFloat(region.revenue)));

    return (
        <Paper sx={{ p: 3, height: 300 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOn sx={{ color: "#ED2024" }} />
                Top Performing Regions
            </Typography>

            <List sx={{ p: 0 }}>
                {topRegions.map((region, index) => {
                    const orderPercentage = (region.orders / maxOrders) * 100;
                    const revenuePercentage = (parseFloat(region.revenue) / maxRevenue) * 100;
                    const intensity = Math.max(20, Math.min(100, revenuePercentage));

                    return (
                        <ListItem
                            key={region.region}
                            sx={{
                                p: 1,
                                mb: 1,
                                borderRadius: 2,
                                background: `linear-gradient(90deg, rgba(237, 32, 36, ${
                                    intensity / 100
                                }) 0%, rgba(237, 32, 36, ${intensity / 200}) 100%)`,
                                color: "white",
                                "&:hover": {
                                    background: `linear-gradient(90deg, rgba(237, 32, 36, ${
                                        Math.min(100, intensity + 10) / 100
                                    }) 0%, rgba(237, 32, 36, ${Math.min(50, intensity / 2 + 5) / 100}) 100%)`,
                                },
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: "rgba(255, 255, 255, 0.2)",
                                    color: "white",
                                    mr: 2,
                                    width: 32,
                                    height: 32,
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                }}
                            >
                                #{index + 1}
                            </Avatar>
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "white",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {region.region}
                                    </Typography>
                                }
                                secondary={
                                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                                        <Chip
                                            label={`${region.orders} orders`}
                                            size="small"
                                            sx={{
                                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                                color: "white",
                                                fontSize: "0.75rem",
                                                height: 20,
                                            }}
                                        />
                                        <Chip
                                            label={`₹${parseFloat(region.revenue).toLocaleString()}`}
                                            size="small"
                                            sx={{
                                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                                color: "white",
                                                fontSize: "0.75rem",
                                                height: 20,
                                            }}
                                        />
                                    </Box>
                                }
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <TrendingUp sx={{ fontSize: 16, color: "rgba(255, 255, 255, 0.8)" }} />
                                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                    {revenuePercentage.toFixed(0)}%
                                </Typography>
                            </Box>
                        </ListItem>
                    );
                })}
            </List>
        </Paper>
    );
};

export default TopRegionsCard;
